export const PROFILE_QUESTION_CODES = [
  "perfil_edad_anos",
  "perfil_altura_cm",
  "perfil_peso_kg",
];

const PROFILE_CODE_SET = new Set(PROFILE_QUESTION_CODES);
const SUPPORTED_TYPES = new Set([
  "OPEN_TEXT",
  "SINGLE_SELECTION",
  "MULTIPLE_SELECTION",
]);

const positiveInteger = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const displayValue = (value) =>
  value === null || value === undefined ? "" : String(value);

export class SurveySchemaError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "SurveySchemaError";
    this.code = code;
  }
}

export function normalizeSurveyQuestions(rawQuestions) {
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) {
    throw new SurveySchemaError(
      "La ficha todavía no tiene preguntas disponibles.",
      "EMPTY_SURVEY"
    );
  }

  const seenIds = new Set();
  const questions = rawQuestions.map((rawQuestion) => {
    if (!rawQuestion || typeof rawQuestion !== "object" || Array.isArray(rawQuestion)) {
      throw new SurveySchemaError(
        "La ficha recibida tiene una pregunta incompleta.",
        "INVALID_QUESTION"
      );
    }
    const id = positiveInteger(rawQuestion && rawQuestion.id);
    if (!id || seenIds.has(id)) {
      throw new SurveySchemaError(
        "La ficha recibida tiene identificadores incompletos.",
        "INVALID_QUESTION_ID"
      );
    }
    seenIds.add(id);

    const type = rawQuestion.tipo;
    if (!SUPPORTED_TYPES.has(type)) {
      throw new SurveySchemaError(
        "La ficha incluye un tipo de pregunta que este formulario aún no puede mostrar. No se guardó ninguna respuesta.",
        "UNSUPPORTED_QUESTION_TYPE"
      );
    }

    const code = typeof rawQuestion.codigo === "string" ? rawQuestion.codigo : "";
    const rawOptions = Array.isArray(rawQuestion.respuestas)
      ? rawQuestion.respuestas
      : [];
    const optionIds = new Set();
    const options = rawOptions.map((rawOption) => {
      const optionId = positiveInteger(rawOption && rawOption.id);
      const label = displayValue(
        rawOption && rawOption.respuesta !== null && rawOption.respuesta !== undefined
          ? rawOption.respuesta
          : rawOption && rawOption.valor
      ).trim();

      if (!optionId || !label) {
        throw new SurveySchemaError(
          "Una pregunta de selección no tiene opciones válidas. No se guardó ninguna respuesta.",
          "INVALID_OPTION"
        );
      }
      if (optionIds.has(optionId)) {
        throw new SurveySchemaError(
          "Una pregunta de selección repite una opción. No se guardó ninguna respuesta.",
          "DUPLICATE_OPTION"
        );
      }
      optionIds.add(optionId);

      return { id: optionId, label };
    });

    if (type !== "OPEN_TEXT" && options.length === 0) {
      throw new SurveySchemaError(
        "Una pregunta de selección no tiene opciones disponibles. No se guardó ninguna respuesta.",
        "EMPTY_OPTIONS"
      );
    }

    if (PROFILE_CODE_SET.has(code) && type !== "OPEN_TEXT") {
      throw new SurveySchemaError(
        "La configuración de las preguntas de perfil no es compatible con el formulario.",
        "INVALID_PROFILE_TYPE"
      );
    }

    return {
      id,
      code,
      prompt: displayValue(rawQuestion.pregunta).trim(),
      type,
      required: PROFILE_CODE_SET.has(code) || rawQuestion.requerida !== false,
      order: Number.isFinite(Number(rawQuestion.orden))
        ? Number(rawQuestion.orden)
        : 0,
      options,
      isProfile: PROFILE_CODE_SET.has(code),
    };
  });

  if (questions.some((question) => !question.prompt)) {
    throw new SurveySchemaError(
      "La ficha incluye una pregunta sin texto. No se guardó ninguna respuesta.",
      "MISSING_QUESTION_TEXT"
    );
  }

  PROFILE_QUESTION_CODES.forEach((code) => {
    const matches = questions.filter((question) => question.code === code);
    if (matches.length !== 1) {
      throw new SurveySchemaError(
        "Faltan metadatos necesarios para validar las medidas de la ficha. Intenta más tarde.",
        "MISSING_PROFILE_METADATA"
      );
    }
  });

  return questions.sort((left, right) => left.order - right.order || left.id - right.id);
}

function profileValueError(question, value) {
  if (!/^\d+$/.test(value)) {
    return `${question.prompt}: escribe un número entero positivo.`;
  }

  const number = Number(value);
  if (!Number.isSafeInteger(number) || number < 1 || number > 32767) {
    return `${question.prompt}: el valor debe estar entre 1 y 32767.`;
  }

  return "";
}

// The mobile caller serializes a selection as the selected option ID string.
function optionResponse(option) {
  return String(option.id);
}

export function validateQuestionAnswer(question, answer) {
  if (question.type === "OPEN_TEXT") {
    const value = typeof answer === "string" ? answer.trim() : "";
    if (question.required && !value) {
      return "Esta respuesta es obligatoria.";
    }
    if (question.isProfile && value) {
      return profileValueError(question, value);
    }
    return "";
  }

  if (question.type === "SINGLE_SELECTION") {
    const selected = positiveInteger(answer);
    const belongsToQuestion = question.options.some((option) => option.id === selected);
    return question.required && !belongsToQuestion
      ? "Selecciona una opción para continuar."
      : !answer || belongsToQuestion
        ? ""
        : "La opción seleccionada ya no está disponible. Elige otra para continuar.";
  }

  if (question.type === "MULTIPLE_SELECTION") {
    const selected = Array.isArray(answer) ? answer : [];
    const selectedIds = selected.map(positiveInteger);
    const hasInvalidOption = selectedIds.some(
      (id) => !id || !question.options.some((option) => option.id === id)
    );
    if (hasInvalidOption) {
      return "Una opción seleccionada ya no está disponible. Revisa tu respuesta.";
    }
    if (question.required && selectedIds.length === 0) {
      return "Selecciona al menos una opción para continuar.";
    }
    if (new Set(selectedIds).size !== selectedIds.length) {
      return "Quita las opciones duplicadas para continuar.";
    }
    return "";
  }

  return "Esta pregunta no se puede validar.";
}

export function buildSurveyPayload(surveyId, questions, answers) {
  const questionErrors = {};
  const responses = [];

  questions.forEach((question) => {
    const answer = answers[question.id];
    const error = validateQuestionAnswer(question, answer);
    if (error) {
      questionErrors[question.id] = error;
      return;
    }

    if (question.type === "OPEN_TEXT") {
      const value = typeof answer === "string" ? answer.trim() : "";
      if (value) {
        responses.push({
          preguntaId: question.id,
          response: value,
          selectedOptionId: null,
        });
      }
      return;
    }

    if (question.type === "SINGLE_SELECTION") {
      const option = question.options.find(
        (candidate) => candidate.id === positiveInteger(answer)
      );
      if (option) {
        responses.push({
          preguntaId: question.id,
          response: optionResponse(option),
          selectedOptionId: option.id,
        });
      }
      return;
    }

    const selectedIds = Array.isArray(answer) ? answer.map(positiveInteger) : [];
    selectedIds.forEach((selectedId) => {
      const option = question.options.find((candidate) => candidate.id === selectedId);
      if (option) {
        responses.push({
          preguntaId: question.id,
          response: optionResponse(option),
          selectedOptionId: option.id,
        });
      }
    });
  });

  if (Object.keys(questionErrors).length > 0) {
    return { errors: questionErrors, payload: null };
  }

  if (responses.length === 0) {
    return {
      errors: {},
      payload: null,
      message: "Agrega las respuestas requeridas antes de enviar la ficha.",
    };
  }

  return {
    errors: {},
    payload: { encuestaId: Number(surveyId), respuestas: responses },
  };
}
